import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ErrorRetry from "@/components/ErrorRetry";
import { usePolling } from "@/lib/usePolling";
import { THREAD_POLL_MS, THREAD_LIST_POLL_MS } from "@/lib/polling";
import {
  CLINIC_CHANNEL_ID, canEditMessage, dmConversationId, isChannel,
} from "@/lib/staffMessages";
import {
  Hash, Loader2, Pencil, Send, Trash2, User, UserPlus, X,
} from "lucide-react";

/** Staff-to-staff messaging: the clinic channel and direct messages.
 *
 *  Lives on the Messages page beside the patient inbox rather than on a route of its
 *  own, so there is one Messages nav item and one badge. Two places in the sidebar
 *  competing for the same word is how people stop trusting either count.
 *
 *  Patients never see any of this. The guarantee is structural, not a filter here: these
 *  rows live in their own collection that no patient-facing endpoint queries. See the
 *  section note in backend/server.py.
 *
 *  `mark_read` is true only when a person OPENS a conversation and false on every poll —
 *  the same contract the patient inbox follows, and here it carries a second
 *  consequence: opening a conversation that references a patient appends an audit entry,
 *  and a poll must not manufacture one every twenty seconds.
 */
export default function TeamMessages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(params.get("conversation") || CLINIC_CHANNEL_ID);
  const [thread, setThread] = useState(null);
  const [colleagues, setColleagues] = useState([]);
  const [patients, setPatients] = useState([]);
  const [picking, setPicking] = useState(false);

  const [body, setBody] = useState("");
  const [tag, setTag] = useState("");
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null);     // message id
  const [editBody, setEditBody] = useState("");

  const [listErr, setListErr] = useState(null);
  const [threadErr, setThreadErr] = useState(null);
  const [sendErr, setSendErr] = useState("");
  const endRef = useRef(null);

  const me = user?.id;

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/staff-messages/conversations");
      setConversations(data);
      setListErr(null);
    } catch (e) {
      setListErr(e);
    }
  }, []);

  /** `markRead` false on polls. See the note above — it decides both the read ticks and
   *  whether a patient-tagged view is audited. */
  const loadThread = useCallback(async (id, { markRead = true, scroll = true } = {}) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/staff-messages/${encodeURIComponent(id)}`,
                                     { params: { mark_read: markRead } });
      setThread(data);
      setThreadErr(null);
      if (scroll) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) {
      setThreadErr(e);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { loadThread(active); }, [active, loadThread]);

  useEffect(() => {
    api.get("/staff").then((r) => setColleagues(r.data.filter((s) => s.id !== me)))
      .catch(() => {});
    api.get("/patients").then((r) => setPatients(r.data.patients || r.data))
      .catch(() => {});
  }, [me]);

  const pollThread = useCallback(
    () => (active ? loadThread(active, { markRead: false, scroll: false }) : undefined),
    [active, loadThread]);
  usePolling(pollThread, THREAD_POLL_MS, { enabled: Boolean(active) });
  usePolling(loadConversations, THREAD_LIST_POLL_MS);

  const open = (id) => {
    setActive(id);
    setEditing(null);
    const next = new URLSearchParams(params);
    next.set("view", "team");
    next.set("conversation", id);
    setParams(next, { replace: true });
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    setSendErr("");
    try {
      await api.post(`/staff-messages/${encodeURIComponent(active)}`,
                     { body, patient_id: tag || null });
      setBody("");          // only after it actually sent
      setTag("");
      await loadThread(active);
      loadConversations();
    } catch (err) {
      // Keep the draft. Losing what somebody typed about a patient is the worst option.
      setSendErr(formatApiError(err.response?.data?.detail) || "That message was not sent.");
    }
    setSending(false);
  };

  const saveEdit = async (message) => {
    if (!editBody.trim()) return;
    setSendErr("");
    try {
      await api.put(`/staff-messages/${encodeURIComponent(active)}/${message.id}`,
                    { body: editBody, patient_id: message.patient_id || null });
      setEditing(null);
      await loadThread(active, { scroll: false });
    } catch (err) {
      setSendErr(formatApiError(err.response?.data?.detail) || "That edit was not saved.");
    }
  };

  const remove = async (message) => {
    setSendErr("");
    try {
      await api.delete(`/staff-messages/${encodeURIComponent(active)}/${message.id}`);
      await loadThread(active, { scroll: false });
      loadConversations();
    } catch (err) {
      setSendErr(formatApiError(err.response?.data?.detail) || "That message was not deleted.");
    }
  };

  /* A brand-new DM has no messages yet, so the server does not list it. Show it anyway
     while it is open: a conversation you are typing into that is missing from the list
     beside it reads as though the message went nowhere. */
  const rows = useMemo(() => {
    if (conversations.some((c) => c.conversation_id === active)) return conversations;
    if (!thread || thread.conversation_id !== active) return conversations;
    return [...conversations, {
      conversation_id: active, kind: thread.kind, title: thread.title,
      participants: thread.participants, unread: 0, last_at: null, last_sender_name: null,
    }];
  }, [conversations, active, thread]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 h-[600px]" data-testid="team-messages">
      {/* Conversations */}
      <div className="bg-card border border-border rounded-lg overflow-y-auto flex flex-col"
           data-testid="team-conversations">
        <div className="p-2 border-b border-border">
          <button data-testid="team-new-dm" onClick={() => setPicking((p) => !p)}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold border border-border rounded-md px-2 py-1.5 hover:bg-secondary">
            {picking ? <X className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
            {picking ? "Cancel" : "Message a colleague"}
          </button>
        </div>

        {picking && (
          <div className="border-b border-border" data-testid="team-colleague-picker">
            {colleagues.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">You're the only one here.</p>
            ) : colleagues.map((c) => (
              <button key={c.id} data-testid={`team-colleague-${c.id}`}
                      onClick={() => { setPicking(false); open(dmConversationId(me, c.id)); }}
                      className="w-full text-left px-4 py-2.5 hover:bg-secondary/40 border-b border-border last:border-0">
                <p className="text-sm font-medium">{c.name}</p>
                <p className="text-xs text-muted-foreground">{ROLE_LABELS[c.role] || c.role}</p>
              </button>
            ))}
          </div>
        )}

        <ErrorRetry
          error={listErr}
          variant="inline"
          onRetry={() => { setListErr(null); loadConversations(); }}
          note="The conversation list may be out of date."
          testid="team-list-error"
        />

        <div className="flex-1">
          {rows.map((c) => (
            <button key={c.conversation_id}
                    data-testid={`team-conversation-${c.conversation_id}`}
                    onClick={() => open(c.conversation_id)}
                    className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/40 transition-colors ${active === c.conversation_id ? "bg-secondary/60" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate inline-flex items-center gap-1.5">
                  {isChannel(c.conversation_id) ? <Hash className="w-3.5 h-3.5 shrink-0" />
                                                : <User className="w-3.5 h-3.5 shrink-0" />}
                  {c.title}
                </span>
                {c.unread > 0 && (
                  <span data-testid={`team-unread-${c.conversation_id}`}
                        className="shrink-0 text-[10px] font-semibold bg-primary text-primary-foreground rounded-full px-1.5 py-0.5">
                    {c.unread}
                  </span>
                )}
              </div>
              {c.last_sender_name && (
                /* Who and when, never a preview: a preview is content on a timer with no
                   audit entry behind it. Content comes from opening the conversation. */
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {c.last_sender_name} · {new Date(c.last_at).toLocaleString()}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* The conversation */}
      <div className="lg:col-span-2 bg-card border border-border rounded-lg flex flex-col">
        {thread ? (
          <>
            <div className="px-5 py-3 border-b border-border">
              <h3 className="font-heading font-semibold inline-flex items-center gap-1.5"
                  data-testid="team-thread-title">
                {isChannel(thread.conversation_id) ? <Hash className="w-4 h-4" /> : <User className="w-4 h-4" />}
                {thread.title}
              </h3>
              {isChannel(thread.conversation_id) && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Everyone on the clinic team. Patients never see this.
                </p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="team-thread-messages">
              {thread.messages.length === 0 && (
                <p className="text-sm text-muted-foreground">Nothing here yet.</p>
              )}
              {thread.messages.map((m) => (
                <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.mine ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}
                       data-testid={`team-message-${m.id}`}>
                    {m.deleted ? (
                      <p className="text-sm italic opacity-70" data-testid={`team-message-deleted-${m.id}`}>
                        Message deleted
                      </p>
                    ) : editing === m.id ? (
                      <div className="space-y-2">
                        <textarea data-testid={`team-edit-input-${m.id}`} rows={2}
                                  value={editBody} onChange={(e) => setEditBody(e.target.value)}
                                  className="w-full rounded-md border border-input bg-card text-foreground px-2 py-1 text-sm outline-none" />
                        <div className="flex gap-2">
                          <button data-testid={`team-edit-save-${m.id}`} onClick={() => saveEdit(m)}
                                  className="text-xs font-semibold bg-card text-foreground rounded px-2 py-1">Save</button>
                          <button onClick={() => setEditing(null)}
                                  className="text-xs opacity-80">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                        {m.patient_id && (
                          /* A LINK to the record, not a copy of anything in it. */
                          <button data-testid={`team-message-tag-${m.id}`}
                                  onClick={() => navigate(`/patients/${m.patient_id}`)}
                                  className={`mt-2 inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 ${m.mine ? "bg-white/20" : "bg-card border border-border"}`}>
                            <User className="w-3 h-3" /> {m.patient_name || "Patient"}
                          </button>
                        )}
                      </>
                    )}
                    <p className={`text-[10px] mt-1 ${m.mine ? "text-white/60" : "text-muted-foreground"}`}>
                      {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                      {m.edited_at && !m.deleted && " · edited"}
                    </p>
                    {canEditMessage(m) && editing !== m.id && (
                      <div className="flex gap-2 mt-1">
                        <button data-testid={`team-edit-${m.id}`}
                                onClick={() => { setEditing(m.id); setEditBody(m.body); }}
                                className="inline-flex items-center gap-1 text-[10px] opacity-80 hover:opacity-100">
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button data-testid={`team-delete-${m.id}`} onClick={() => remove(m)}
                                className="inline-flex items-center gap-1 text-[10px] opacity-80 hover:opacity-100">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="px-3 pt-3 space-y-2">
              <ErrorRetry
                error={threadErr}
                variant="inline"
                onRetry={() => { setThreadErr(null); loadThread(active); }}
                note="This conversation may be out of date."
                testid="team-thread-error"
              />
              {sendErr && (
                <p className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded px-2 py-1"
                   data-testid="team-send-error">{sendErr}</p>
              )}
            </div>

            <form onSubmit={send} className="p-3 border-t border-border space-y-2">
              <div className="flex gap-2">
                <input data-testid="team-message-input" value={body}
                       onChange={(e) => setBody(e.target.value)}
                       placeholder="Message the team…"
                       className="flex-1 rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
                <button data-testid="team-send" disabled={sending || !body.trim()}
                        className="bg-primary text-primary-foreground rounded-md px-4 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center gap-1.5">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                About a patient?
                <select data-testid="team-tag-patient" value={tag}
                        onChange={(e) => setTag(e.target.value)}
                        className="rounded-md border border-input bg-card px-2 py-1 text-xs">
                  <option value="">No patient</option>
                  {patients.map((p) => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
              </label>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            <div className="text-center"><Hash className="w-8 h-8 mx-auto mb-2 opacity-40" />Select a conversation</div>
          </div>
        )}
      </div>
    </div>
  );
}
